// VRG-744 — Competitor research (internal). API client for `/internal/competitor-research/*`.
//
// Types are declared locally, as `seoApi.ts` does: apps/platform does not depend on @repo/types
// for internal endpoints. Canonical definitions:
// `apps/api/src/features/competitor-research/competitor-research.types.ts`. Keep in step.

import { serverErrorMessage } from 'utils/serverErrorMessage'

import { createApiCall } from './index'

export type CompetitorBillingPeriod = 'month' | 'year' | 'one_time' | 'usage' | 'custom'
export type FeatureCellValue = 'yes' | 'partial' | 'no' | 'unknown'
export type VeraFeatureStatus = 'shipped' | 'planned'

export interface ResearchCompetitor {
  domain: string
  name: string | null
  kind: 'business' | 'search' | 'adjacent'
  /** The profile's one-line pricing summary (`seo_competitors.pricing`). */
  pricingSummary: string | null
  /** "N of 10", LOWER = more dangerous; null = not judged yet. Orders the matrix's columns. */
  riskRank: number | null
}

export interface CompetitorPrice {
  id: string
  competitorDomain: string
  planName: string
  /** null = no public figure ("contact sales"). */
  price: number | null
  currency: string
  billingPeriod: CompetitorBillingPeriod
  included: string | null
  sourceUrl: string
  capturedAt: string
  capturedBy: string
  status: 'current' | 'superseded'
  supersededBy: string | null
  supersededAt: string | null
  createdAt: string
}

export interface PricingResponse {
  competitors: ResearchCompetitor[]
  prices: CompetitorPrice[]
  capture: { available: boolean; note: string }
}

export interface CompetitorFeature {
  id: string
  slug: string
  category: string
  name: string
  description: string | null
  sortOrder: number
  veraStatus: VeraFeatureStatus | null
  veraNote: string | null
  veraSource: string | null
  webList: 'compare' | 'pending' | null
  webToday: string | null
  webIcon: string | null
  webEvidence: string | null
  updatedAt: string
  updatedBy: string | null
}

export interface FeatureCell {
  competitorDomain: string
  featureId: string
  value: FeatureCellValue
  note: string | null
  sourceUrl: string | null
  capturedAt: string | null
  capturedBy: string | null
  updatedAt: string
}

export interface FeaturesResponse {
  competitors: ResearchCompetitor[]
  features: CompetitorFeature[]
  cells: FeatureCell[]
}

export interface CompetitorContentItem {
  id: string
  title: string | null
  url: string
  publication: string | null
  publishedAt: string | null
  retrievedAt: string
  status: string
  sourceName: string | null
  competitorDomain: string
  competitorName: string | null
}

export interface CompetitorContentResponse {
  basis: 'feed' | 'host'
  items: CompetitorContentItem[]
}

export interface CompetitorSeoRow extends ResearchCompetitor {
  rankedKeywords: number
  sharedWithVera: number
  aheadOfVera: number
  avgPosition: number | null
  gapKeywords: number
  topGapKeywords: Array<{ keyword: string; position: number | null; volume: number | null }>
}

export interface CompetitorSeoResponse {
  market: string
  markets: Array<{ code: string; name: string }>
  keywordsChecked: number
  lastCheckAt: string | null
  rows: CompetitorSeoRow[]
}

export interface PriceInput {
  planName: string
  price: number | null
  currency: string
  billingPeriod: CompetitorBillingPeriod
  included: string | null
  sourceUrl: string
  capturedAt: string
}

export interface CellInput {
  value: FeatureCellValue
  note: string | null
  sourceUrl: string | null
  capturedAt: string | null
}

/**
 * `createApiCall` REJECTS on an error status (services/index.ts `rejectWithResponse`), with the
 * axios error — whose own `.message` is "Request failed with status code 400". So the server's
 * reason ("A competitor is described, never judged…") is read off the response body here, or the
 * copy rules would refuse a write without saying which rule. The status check after the await
 * stays for any response that still resolves.
 */
async function call<T>(method: 'GET' | 'POST' | 'PATCH' | 'PUT', url: string, what: string, data?: unknown): Promise<T> {
  const fail = (status: number | undefined, body: { message?: unknown } | undefined): never => {
    const raw = body?.message
    const error = new Error(
      serverErrorMessage({
        message: Array.isArray(raw) ? raw.join(' ') : typeof raw === 'string' ? raw : undefined,
        status,
        fallback: method === 'GET' ? `Could not load ${what}.` : `Could not save ${what}.`,
      }),
    ) as Error & { status?: number }
    error.status = status ?? 0
    throw error
  }
  let response
  try {
    response = await createApiCall({ method, url, data: data ?? {} })
  } catch (e) {
    const err = e as { status?: number; response?: { status?: number; data?: { message?: unknown } } }
    return fail(err.status ?? err.response?.status, err.response?.data)
  }
  if (response.status < 200 || response.status >= 300) return fail(response.status, response.data)
  return response.data as T
}

const BASE = '/internal/competitor-research'
const enc = encodeURIComponent

export const competitorResearchApi = {
  pricing: (): Promise<PricingResponse> => call('GET', `${BASE}/pricing`, 'competitor pricing'),
  features: (): Promise<FeaturesResponse> => call('GET', `${BASE}/features`, 'feature matrix'),
  content: (): Promise<CompetitorContentResponse> => call('GET', `${BASE}/content`, 'competitor content'),
  seo: (market: string): Promise<CompetitorSeoResponse> =>
    call('GET', `${BASE}/seo?market=${enc(market)}`, 'competitor SEO'),

  // Writes — platform_admin only on the server.
  addPrice: (domain: string, input: PriceInput): Promise<{ price: CompetitorPrice; superseded: string | null }> =>
    call('POST', `${BASE}/competitors/${enc(domain)}/prices`, 'price', input),
  retirePrice: (id: string): Promise<CompetitorPrice> => call('POST', `${BASE}/prices/${enc(id)}/retire`, 'price'),
  createFeature: (input: { name: string; category: string; description: string | null }): Promise<CompetitorFeature> =>
    call('POST', `${BASE}/features`, 'feature', input),
  patchFeature: (id: string, patch: Partial<Pick<CompetitorFeature, 'name' | 'description' | 'category' | 'veraStatus' | 'veraNote' | 'veraSource'>>): Promise<CompetitorFeature> =>
    call('PATCH', `${BASE}/features/${enc(id)}`, 'feature', patch),
  setCell: (featureId: string, domain: string, input: CellInput): Promise<FeatureCell> =>
    call('PUT', `${BASE}/features/${enc(featureId)}/cells/${enc(domain)}`, 'feature cell', input),
}
